/*
 * widget-defer.js — runtime engine for the widget-defer orchestrator.
 *
 * Depends on window.__widgetDeferRegistry (from widget-defer-registry.js) being
 * loaded before this script. Both must be inlined in <head> BEFORE
 * {{ content_for_header }} so the interceptor is active before any vendor
 * script tags are added to the DOM.
 *
 * Reads:
 *   window.__widgetDeferRegistry — full registry (all known widgets)
 *   window.__widgetDeferQueue    — list of widget_ids active on this page
 *                                  (populated by snippets/widget-defer.liquid renders)
 *
 * See pdp-load-audit/docs/WIDGET-ORCHESTRATOR-SPEC.md for architecture.
 * Rule: EVERY operation in the interceptor path is wrapped in try/catch —
 * a failure in one widget's handling must not cascade to break others.
 */

(function () {
  'use strict';

  var REGISTRY = window.__widgetDeferRegistry || [];
  var QUEUE_KEY = '__widgetDeferQueue';
  var DEFERRED_SCRIPTS = {};      // { widget_id: [src, ...] } — collected by interceptor
  var PENDING_CLICKS = {};        // { widget_id: true } — clicks arrived before hydration
  var HYDRATED = {};              // { widget_id: true } — one-shot per widget

  /* =====================================================================
   * SECTION 1 — Interceptor
   * Runs immediately on script execution. MutationObserver watches every
   * added node; if a <script src> matches a registry sourcePattern, it's
   * removed before execution (browser may still fetch the bytes, but no
   * code runs = main-thread reclaimed).
   * =================================================================== */

  function initInterceptor() {
    try {
      if (typeof MutationObserver === 'undefined') return;
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            try {
              if (n.nodeName !== 'SCRIPT') continue;
              if (!n.src) continue;
              if (n.hasAttribute('data-aro-hydrated')) continue;         /* our own re-inject */
              if (n.hasAttribute('data-aro-deferred-src')) continue;     /* already handled */
              for (var k = 0; k < REGISTRY.length; k++) {
                var entry = REGISTRY[k];
                if (!entry.injection || entry.injection.path !== 'script-src') continue;
                if (!entry.injection.sourcePattern || !entry.injection.sourcePattern.test(n.src)) continue;
                /* Viewport check — skip defer if widget wouldn't run on this viewport anyway */
                var vp = entry.viewport || {};
                var w = window.innerWidth;
                if (vp.min && w < vp.min) continue;
                if (vp.max && w > vp.max) continue;
                /* Match — defer it */
                DEFERRED_SCRIPTS[entry.id] = DEFERRED_SCRIPTS[entry.id] || [];
                if (DEFERRED_SCRIPTS[entry.id].indexOf(n.src) === -1) {
                  DEFERRED_SCRIPTS[entry.id].push(n.src);
                }
                n.setAttribute('data-aro-deferred-src', n.src);
                n.setAttribute('data-aro-deferred-id', entry.id);
                n.type = 'text/aro-deferred';
                n.remove();
                break;
              }
            } catch (e) { /* observer must never throw */ }
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* interceptor init failed — vendor scripts load normally, no harm */ }
  }

  initInterceptor();

  /* =====================================================================
   * SECTION 2 — Trigger dispatcher
   * Each trigger has signature (entry, onFire) — attaches its listener +
   * calls onFire() when the trigger condition is met. Adding a new trigger
   * = adding a new key here; no other runtime changes needed.
   * =================================================================== */

  var TRIGGERS = {
    'natural': function (entry, onFire) {
      /* Load at "natural" time — as soon as DCL fires. Vendor script would
         have run at parse-time if not intercepted; hydrate now to restore
         approximate original timing. */
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { onFire(); }, { once: true });
      } else {
        onFire();
      }
    },

    'post-lcp-idle': function (entry, onFire) {
      var fired = false;
      var fire = function () { if (fired) return; fired = true; onFire(); };
      try {
        if (typeof PerformanceObserver !== 'undefined') {
          var po = new PerformanceObserver(function () {
            po.disconnect();
            if (typeof requestIdleCallback === 'function') {
              requestIdleCallback(fire, { timeout: 3000 });
            } else {
              setTimeout(fire, 500);
            }
          });
          po.observe({ type: 'largest-contentful-paint', buffered: true });
        }
      } catch (e) { /* fall through */ }
      /* Safety timeout — cap at 5s from now regardless of LCP fire */
      setTimeout(fire, 5000);
    },

    'scroll-into-view': function (entry, onFire) {
      var sel = entry.placeholder && entry.placeholder.selector;
      if (!sel) { onFire(); return; }
      var attach = function (target) {
        try {
          var io = new IntersectionObserver(function (entries) {
            if (entries.some(function (e) { return e.isIntersecting; })) {
              io.disconnect();
              onFire();
            }
          }, { rootMargin: '200px' });
          io.observe(target);
        } catch (e) { onFire(); }
      };
      var el = document.querySelector(sel);
      if (el) { attach(el); return; }
      /* Poll — placeholder may not have rendered yet */
      var tries = 0;
      var poll = setInterval(function () {
        if (++tries > 40) { clearInterval(poll); return; }
        var e = document.querySelector(sel);
        if (e) { clearInterval(poll); attach(e); }
      }, 500);
    },

    'first-interaction-or-idle': function (entry, onFire) {
      /* Fire on whichever comes first: user interaction, LCP+idle, or 5s
         safety cap. Rationale for Clarity et al: engaged users trigger a
         near-immediate load (session captured with basically nothing missed);
         idle readers load after LCP+idle so layout has settled; pure bounces
         under 5s aren't captured, which is acceptable (bounce data isn't
         valuable session-recording input). Combines the responsiveness of
         first-interaction gating with the layout-settle guarantee of
         post-lcp-idle. */
      var fired = false;
      var listeners = [];
      var fire = function () {
        if (fired) return;
        fired = true;
        /* Remove all pending listeners so we don't hold event handler refs */
        for (var i = 0; i < listeners.length; i++) {
          try { window.removeEventListener(listeners[i][0], listeners[i][1], listeners[i][2]); } catch (e) {}
        }
        onFire();
      };
      /* Defer to next paint task — otherwise the vendor script's post-fetch
         parse/execute/DOM-scans run on the same main-thread frame as the
         interaction's paint, inflating INP. Idle path fires directly since
         there's no interaction to protect. Regression discovered 2026-09-01. */
      var fireDeferred = function () {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(function () { setTimeout(fire, 0); });
        } else {
          setTimeout(fire, 0);
        }
      };
      var events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      for (var i = 0; i < events.length; i++) {
        var opts = { once: true, passive: true, capture: true };
        listeners.push([events[i], fireDeferred, opts]);
        try { window.addEventListener(events[i], fireDeferred, opts); } catch (e) {}
      }
      /* LCP + idle callback path — for users who never interact */
      try {
        if (typeof PerformanceObserver !== 'undefined') {
          var po = new PerformanceObserver(function () {
            po.disconnect();
            if (typeof requestIdleCallback === 'function') {
              requestIdleCallback(fire, { timeout: 3000 });
            } else {
              setTimeout(fire, 500);
            }
          });
          po.observe({ type: 'largest-contentful-paint', buffered: true });
        }
      } catch (e) {}
      /* Safety cap — always fire by 5s regardless of anything else */
      setTimeout(fire, 5000);
    },
  };

  /* =====================================================================
   * SECTION 3 — Hydration
   * Called when any trigger fires. Injects the deferred scripts, applies
   * configReassert if any, calls initApi, applies position overrides.
   * =================================================================== */

  function resolvePath(path) {
    /* 'window.reamaze.initialize' → the function ref (or null) */
    if (!path) return null;
    var parts = path.split('.');
    if (parts[0] === 'window') parts.shift();
    var obj = window;
    for (var i = 0; i < parts.length; i++) {
      if (obj == null) return null;
      obj = obj[parts[i]];
    }
    return obj;
  }

  function deepAssign(target, source) {
    for (var k in source) {
      if (!Object.prototype.hasOwnProperty.call(source, k)) continue;
      if (typeof source[k] === 'object' && source[k] !== null && !Array.isArray(source[k])) {
        target[k] = target[k] || {};
        deepAssign(target[k], source[k]);
      } else {
        target[k] = source[k];
      }
    }
    return target;
  }

  function applyConfigReassert(entry) {
    if (!entry.configReassert) return;
    try {
      for (var globalName in entry.configReassert.config) {
        window[globalName] = window[globalName] || {};
        deepAssign(window[globalName], entry.configReassert.config[globalName]);
      }
      if (entry.configReassert.reinvokeInitApi && entry.hydration.initApi) {
        var fn = resolvePath(entry.hydration.initApi);
        if (typeof fn === 'function') { try { fn(); } catch (e) {} }
      }
    } catch (e) {}
  }

  function scheduleConfigReassertLadder(entry) {
    if (!entry.configReassert || !entry.configReassert.at) return;
    entry.configReassert.at.forEach(function (delay) {
      if (delay === 0) { applyConfigReassert(entry); return; }
      setTimeout(function () { applyConfigReassert(entry); }, delay);
    });
  }

  function injectDeferredScripts(entry) {
    var srcs = DEFERRED_SCRIPTS[entry.id] || [];
    if (!srcs.length) {
      /* Interceptor didn't catch anything — either the vendor didn't inject
         yet, or entry.mode === 'timing-only' with nothing to defer. Return
         resolved so downstream hydration steps still run (e.g. skeleton
         placeholder gets hidden). */
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var remaining = srcs.length;
      srcs.forEach(function (src) {
        try {
          var s = document.createElement('script');
          s.setAttribute('data-aro-hydrated', '1');
          s.src = src;
          s.async = false;                                              /* preserve execution order */
          s.onload = s.onerror = function () { if (--remaining === 0) resolve(); };
          document.head.appendChild(s);
        } catch (e) { if (--remaining === 0) resolve(); }
      });
    });
  }

  function applyPositionOverride(entry) {
    if (!entry.position || !entry.placeholder || !entry.placeholder.selector) return;
    var sel = entry.placeholder.selector;
    var apply = function (el) {
      try {
        for (var k in entry.position) {
          if (k === 'viewport') continue;
          el.style.setProperty(k, entry.position[k], 'important');
        }
      } catch (e) {}
    };
    /* Try immediately, else watch for mount */
    var el = document.querySelector(sel + ':not([data-widget-defer-placeholder])');
    if (el) { apply(el); return; }
    var obs = new MutationObserver(function () {
      var e = document.querySelector(sel + ':not([data-widget-defer-placeholder])');
      if (e) { obs.disconnect(); apply(e); }
    });
    try { obs.observe(document.body, { childList: true, subtree: true }); } catch (e) { return; }
    setTimeout(function () { try { obs.disconnect(); } catch (e) {} }, 15000);   /* bounded */
  }

  function hidePlaceholder(entry) {
    /* Hide our placeholder ONCE the real widget mounts — never on a blind
       timer. Placeholder must stay visible until we can confirm the real
       widget is there, otherwise we get a visible gap (or if hydration
       fails entirely, the placeholder never comes back and the user sees
       nothing at all). */
    if (!entry.placeholder || !entry.placeholder.selector) return;
    var sel = entry.placeholder.selector;
    var placeholderSel = sel + '[data-widget-defer-placeholder]';
    var realSel = sel + ':not([data-widget-defer-placeholder])';

    /* reuseInPlace mode: vendor reuses the placeholder element rather than
       creating its own (Reamaze, PushOwl). No "real widget" ever mounts at
       a separate selector, so tryHide's :not([placeholder]) polling never
       matches. Instead just strip the placeholder marker so the element
       becomes indistinguishable from a vendor-native mount — visible bell/
       bubble stays in place, engine's other placeholder-scoped logic won't
       target it any more. Click handling is done via clickHandler (see
       setupClickForward) since vendor's own listener attach is unreliable
       across viewports / permission states. */
    if (entry.hydration && entry.hydration.reuseInPlace) {
      try {
        var el = document.querySelector(placeholderSel);
        if (el) el.removeAttribute('data-widget-defer-placeholder');
      } catch (e) {}
      return;
    }

    var tryHide = function () {
      try {
        var real = document.querySelector(realSel);
        if (!real) return false;
        /* Real widget must have non-zero rendered dimensions before we hide
           the placeholder — some vendors mount an empty container first
           then populate it. If we hide too early we get a gap. */
        var r = real.getBoundingClientRect();
        if (r.width < 1 || r.height < 1) return false;
        var placeholder = document.querySelector(placeholderSel);
        if (placeholder) placeholder.style.setProperty('display', 'none', 'important');
        return true;
      } catch (e) { return false; }
    };

    if (tryHide()) return;
    /* Watch for the real widget to mount. Bounded 20s — if vendor never
       mounts, placeholder stays visible (safer than a persistent blank). */
    var obs;
    try {
      obs = new MutationObserver(function () { if (tryHide()) obs.disconnect(); });
      obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
      setTimeout(function () { try { obs.disconnect(); } catch (e) {} }, 20000);
    } catch (e) { /* observer setup failed — placeholder stays, harmless */ }
  }

  function replayPendingClick(entry) {
    /* reuseInPlace widgets use clickHandler for click delivery; no separate
       real element ever exists to replay against. */
    if (entry.hydration && entry.hydration.reuseInPlace) return;
    if (!entry.hydration.clickForward) return;
    if (!PENDING_CLICKS[entry.id]) return;
    var sel = entry.placeholder && entry.placeholder.selector;
    if (!sel) return;
    var tries = 0;
    var poll = setInterval(function () {
      if (++tries > 40) { clearInterval(poll); return; }
      var el = document.querySelector(sel + ':not([data-widget-defer-placeholder])');
      if (el) {
        clearInterval(poll);
        try { el.click(); } catch (e) {}
      }
    }, 200);
  }

  function hydrate(entry) {
    if (HYDRATED[entry.id]) return;
    HYDRATED[entry.id] = true;
    injectDeferredScripts(entry).then(function () {
      applyConfigReassert(entry);
      var initFn = resolvePath(entry.hydration.initApi);
      if (typeof initFn === 'function') { try { initFn(); } catch (e) {} }
      scheduleConfigReassertLadder(entry);
      applyPositionOverride(entry);
      hidePlaceholder(entry);
      replayPendingClick(entry);
    });
  }

  /* =====================================================================
   * SECTION 4 — Click-forward
   * Delegated click listener at document level. If a click lands on a
   * placeholder (marked via data-widget-defer-placeholder) before hydration,
   * flag it; hydrate() will replay after real widget mounts.
   * =================================================================== */

  function setupClickForward(entry) {
    /* reuseInPlace + clickHandler: attach a delegated click listener that
       calls the per-widget handler function (registry-provided) on every
       matching click. Persistent, no PENDING_CLICKS queue. Handler is
       responsible for gating (e.g. skip if vendor already opened its UI,
       check permission state, etc.). Matches BOTH placeholder-marked
       elements AND the same element after data-widget-defer-placeholder
       is stripped (post-hydration) — so click stays wired for the lifetime
       of the page. */
    if (entry.hydration && entry.hydration.reuseInPlace && typeof entry.hydration.clickHandler === 'function') {
      var sel = entry.placeholder && entry.placeholder.selector;
      if (!sel) return;
      document.addEventListener('click', function (evt) {
        try {
          var target = evt.target;
          while (target && target !== document.body) {
            if (target.matches && target.matches(sel)) {
              try { entry.hydration.clickHandler(evt, entry); } catch (e) {}
              break;
            }
            target = target.parentNode;
          }
        } catch (e) {}
      }, true);
      return;
    }

    if (!entry.hydration.clickForward) return;
    if (!entry.placeholder || !entry.placeholder.selector) return;
    document.addEventListener('click', function (evt) {
      try {
        var target = evt.target;
        while (target && target !== document.body) {
          if (target.matches && target.matches(entry.placeholder.selector) && target.hasAttribute('data-widget-defer-placeholder')) {
            PENDING_CLICKS[entry.id] = true;
            break;
          }
          target = target.parentNode;
        }
      } catch (e) {}
    }, true);
  }

  /* =====================================================================
   * SECTION 5 — Registration
   * For each widget_id in the queue, find its registry entry, mark the
   * rendered placeholder, wire up click-forward + hydration triggers.
   * =================================================================== */

  function markPlaceholders() {
    var queue = window[QUEUE_KEY] || [];
    queue.forEach(function (id) {
      try {
        var entry = REGISTRY.filter(function (e) { return e.id === id; })[0];
        if (!entry || !entry.placeholder) return;
        var el = document.querySelector(entry.placeholder.selector);
        if (el) el.setAttribute('data-widget-defer-placeholder', id);
      } catch (e) {}
    });
  }

  function activate() {
    var queue = window[QUEUE_KEY] || [];
    queue.forEach(function (id) {
      try {
        var entry = REGISTRY.filter(function (e) { return e.id === id; })[0];
        if (!entry) return;
        var vp = entry.viewport || {};
        var w = window.innerWidth;
        if (vp.min && w < vp.min) return;
        if (vp.max && w > vp.max) return;
        setupClickForward(entry);
        var triggers = entry.hydration && entry.hydration.triggers || [];
        triggers.forEach(function (name) {
          var handler = TRIGGERS[name];
          if (!handler) return;
          try { handler(entry, function () { hydrate(entry); }); } catch (e) {}
        });
      } catch (e) {}
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { markPlaceholders(); activate(); });
  } else {
    markPlaceholders(); activate();
  }
})();
